/**
 * FingerprintJS Pro SPA v0.2.4 - Copyright (c) FingerprintJS, Inc, 2022 (https://fingerprintjs.com)
 * Licensed under the MIT (http://www.opensource.org/licenses/mit-license.php) license.
 */

'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var tslib = require('tslib');
var FingerprintJS = require('@fingerprintjs/fingerprintjs-pro');

function _interopNamespace(e) {
    if (e && e.__esModule) return e;
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n["default"] = e;
    return Object.freeze(n);
}

var FingerprintJS__namespace = /*#__PURE__*/_interopNamespace(FingerprintJS);

const CACHE_KEY_PREFIX = '@fpjs@client@';
const MAX_CACHE_LIFE = 60 * 60 * 24; // 24 hours
const DEFAULT_CACHE_LIFE = 60 * 60; // 1 hour
const DEFAULT_NOW_PROVIDER = () => Date.now();
class CacheKey {
    constructor(options) {
        var _a;
        this.tag = options.tag || null;
        this.linkedId = options.linkedId || null;
        this.extendedResult = (_a = options.extendedResult) !== null && _a !== void 0 ? _a : false;
    }
    /**
     * Converts this `CacheKey` instance into a string for use in a cache
     * @returns A string representation of the key
     */
    toKey() {
        return `${JSON.stringify(this.tag)}__${JSON.stringify(this.linkedId)}__${this.extendedResult}`;
    }
}
function getKeyWithPrefix(key, prefix) {
    return `${prefix}__${key}`;
}
function removePrefixFromKey(key, prefix) {
    return key.replace(`${prefix}__`, '');
}

class LocalStorageCache {
    constructor(prefix = CACHE_KEY_PREFIX) {
        this.prefix = prefix;
    }
    set(key, entry) {
        window.localStorage.setItem(getKeyWithPrefix(key, this.prefix), JSON.stringify(entry));
    }
    get(key) {
        const json = window.localStorage.getItem(getKeyWithPrefix(key, this.prefix));
        if (!json) {
            return;
        }
        try {
            return JSON.parse(json);
        }
        catch (e) {
            return;
        }
    }
    remove(key) {
        window.localStorage.removeItem(getKeyWithPrefix(key, this.prefix));
    }
    allKeys() {
        return Object.keys(window.localStorage)
            .filter((key) => key.startsWith(this.prefix))
            .map((key) => removePrefixFromKey(key, this.prefix));
    }
}

class SessionStorageCache {
    constructor(prefix = CACHE_KEY_PREFIX) {
        this.prefix = prefix;
    }
    set(key, entry) {
        window.sessionStorage.setItem(getKeyWithPrefix(key, this.prefix), JSON.stringify(entry));
    }
    get(key) {
        const json = window.sessionStorage.getItem(getKeyWithPrefix(key, this.prefix));
        if (!json) {
            return;
        }
        try {
            return JSON.parse(json);
        }
        catch (e) {
            return;
        }
    }
    remove(key) {
        window.sessionStorage.removeItem(getKeyWithPrefix(key, this.prefix));
    }
    allKeys() {
        return Object.keys(window.sessionStorage)
            .filter((key) => key.startsWith(this.prefix))
            .map((key) => removePrefixFromKey(key, this.prefix));
    }
}

class CacheManager {
    constructor(cache, cacheTime = DEFAULT_CACHE_LIFE, nowProvider) {
        this.cache = cache;
        this.cacheTime = cacheTime;
        this.nowProvider = nowProvider || DEFAULT_NOW_PROVIDER;
    }
    get(cacheKey) {
        return tslib.__awaiter(this, void 0, void 0, function* () {
            const wrappedEntry = yield this.cache.get(cacheKey.toKey());
            if (!wrappedEntry) {
                return;
            }
            const now = yield this.nowProvider();
            const nowSeconds = Math.floor(now / 1000);
            if (wrappedEntry.expiresAt < nowSeconds) {
                yield this.cache.remove(cacheKey.toKey());
                return;
            }
            return wrappedEntry.body;
        });
    }
    set(cacheKey, entry) {
        return tslib.__awaiter(this, void 0, void 0, function* () {
            const wrappedEntry = yield this.wrapCacheEntry(entry);
            yield this.cache.set(cacheKey.toKey(), wrappedEntry);
        });
    }
    clearCache() {
        return tslib.__awaiter(this, void 0, void 0, function* () {
            const keys = yield this.cache.allKeys();
            yield Promise.all(keys.map((key) => this.cache.remove(key)));
        });
    }
    wrapCacheEntry(entry) {
        return tslib.__awaiter(this, void 0, void 0, function* () {
            const now = yield this.nowProvider();
            const expiresInTime = Math.floor(now / 1000) + this.cacheTime;
            return {
                body: entry,
                expiresAt: expiresInTime,
            };
        });
    }
}

class InMemoryCache {
    constructor() {
        this.enclosedCache = (function () {
            const cache = {};
            return {
                set(key, entry) {
                    cache[key] = entry;
                },
                get(key) {
                    const cacheEntry = cache[key];
                    if (!cacheEntry) {
                        return;
                    }
                    return cacheEntry;
                },
                remove(key) {
                    delete cache[key];
                },
                allKeys() {
                    return Object.keys(cache);
                },
            };
        })();
    }
}

class CacheStub {
    set() { }
    get() {
        return undefined;
    }
    remove() { }
    allKeys() {
        return [];
    }
}

exports.CacheLocation = void 0;
(function (CacheLocation) {
    CacheLocation["Memory"] = "memory";
    CacheLocation["LocalStorage"] = "localstorage";
    CacheLocation["SessionStorage"] = "sessionstorage";
    CacheLocation["NoCache"] = "nocache";
})(exports.CacheLocation || (exports.CacheLocation = {}));

const cacheLocationBuilders = {
    [exports.CacheLocation.Memory]: () => new InMemoryCache().enclosedCache,
    [exports.CacheLocation.LocalStorage]: (prefix) => new LocalStorageCache(prefix),
    [exports.CacheLocation.SessionStorage]: (prefix) => new SessionStorageCache(prefix),
    [exports.CacheLocation.NoCache]: () => new CacheStub(),
};
const cacheFactory = (location) => {
    return cacheLocationBuilders[location];
};
/**
 * FingerprintJS SDK for Single Page Applications
 */
class FpjsClient {
    constructor(options) {
        var _a;
        this.options = options;
        this.inFlightRequests = new Map();
        this.agentPromise = null;
        this.agent = {
            get: () => {
                throw new Error("FPJSAgent hasn't loaded yet. Make sure to call the init() method first.");
            },
        };
        this.loadOptions = options.loadOptions;
        if (options.cache && options.cacheLocation) {
            console.warn('Both `cache` and `cacheLocation` options have been specified in the FpjsClient configuration; ignoring `cacheLocation` and using `cache`.');
        }
        let cache;
        if (options.cache) {
            cache = options.cache;
        }
        else {
            this.cacheLocation = options.cacheLocation || exports.CacheLocation.SessionStorage;
            if (!cacheFactory(this.cacheLocation)) {
                throw new Error(`Invalid cache location "${this.cacheLocation}"`);
            }
            cache = cacheFactory(this.cacheLocation)(options.cachePrefix);
        }
        if (options.cacheTimeInSeconds && options.cacheTimeInSeconds > MAX_CACHE_LIFE) {
            throw new Error(`Cache time cannot exceed 86400 seconds (24 hours)`);
        }
        const cacheTime = (_a = options.cacheTimeInSeconds) !== null && _a !== void 0 ? _a : DEFAULT_CACHE_LIFE;
        this.cacheManager = new CacheManager(cache, cacheTime);
    }
    /**
     * Loads FPJS JS agent with certain settings and stores the instance in memory
     * [https://dev.fingerprintjs.com/docs/js-agent#agent-initialization]
     */
    init() {
        return tslib.__awaiter(this, void 0, void 0, function* () {
            if (!this.agentPromise) {
                this.agentPromise = FingerprintJS__namespace.load(this.loadOptions).then((agent) => {
                    this.agent = agent;
                    return agent;
                });
            }
            return this.agentPromise;
        });
    }
    /**
     * Returns visitor identification data based on the request options
     * [https://dev.fingerprintjs.com/docs/js-agent#visitor-identification]
     *
     * @param options
     * @param ignoreCache if set to true a request to the API will be made even if the data is present in cache
     */
    getVisitorData(options = {}, ignoreCache = false) {
        return tslib.__awaiter(this, void 0, void 0, function* () {
            const cacheKey = FpjsClient.makeCacheKey(options);
            const key = cacheKey.toKey();
            if (!this.inFlightRequests.has(key)) {
                const promise = this._identify(options, ignoreCache).then((visitorData) => {
                    this.inFlightRequests.delete(key);
                    return visitorData;
                });
                this.inFlightRequests.set(key, promise);
            }
            return (yield this.inFlightRequests.get(key));
        });
    }
    /**
     * Clears visitor data from cache regardless of the cache implementation
     */
    clearCache() {
        return tslib.__awaiter(this, void 0, void 0, function* () {
            yield this.cacheManager.clearCache();
        });
    }
    /**
     * Makes a CacheKey object from GetOptions
     */
    static makeCacheKey(options) {
        return new CacheKey(options);
    }
    _identify(options, ignoreCache = false) {
        return tslib.__awaiter(this, void 0, void 0, function* () {
            const key = FpjsClient.makeCacheKey(options);
            if (!ignoreCache) {
                const cacheResult = yield this.cacheManager.get(key);
                if (cacheResult) {
                    return cacheResult;
                }
            }
            const agentResult = yield this.agent.get(options);
            yield this.cacheManager.set(key, agentResult);
            return agentResult;
        });
    }
}

exports.CacheStub = CacheStub;
exports.FpjsClient = FpjsClient;
exports.InMemoryCache = InMemoryCache;
exports.LocalStorageCache = LocalStorageCache;
exports.SessionStorageCache = SessionStorageCache;



/** WEBPACK FOOTER **
 ** ./~/@fingerprintjs/fingerprintjs-pro-spa/dist/fp-pro-spa.cjs.js
 **/